/**
 * Pre-flight permission probe for the in-embed MAUI space picker.
 *
 * Hits the same `SearchConfluenceSpace.spaceEntity.operations` field
 * the ephemeral preview's `useConfluenceEntities` consumes, so picker
 * behaviour matches Smart Create 1:1.
 *
 * Exposes:
 * - `canCreatePageInSpace(cloudId, spaceKey)` — async permission check.
 * - `getCurrentSpaceSnapshot(cloudId, spaceKey)` — sync getter the picker
 *   uses to fill its `currentSpace` chip from the cache populated as a
 *   side-effect of the async probe.
 *
 * The picker always mounts AFTER the probe has run (it only mounts when
 * the probe returns `false`), so a single shared module-scoped snapshot
 * cache is enough to keep the chip rendering fully populated.
 */
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

export type ResolvedSpace = {
	canCreatePage: boolean;
	iconPath?: string;
	id: string;
	key: string;
	name: string;
	type: string;
};

// Inlined `space` branch of `ListEntitiesQuery` from
// `@atlassian/conversation-assistant-service`.
const ListSpacesWithOperationsQuery = `
query MauiFinalizeListSpacesWithOperations(
	$query: String!
	$siteAri: String!
	$entityAtis: [String!]!
) {
	search {
		search(
			query: $query
			filters: { entities: $entityAtis, locations: [$siteAri] }
			experience: "rovo-chat-message-actions"
		) {
			edges {
				node {
					type
					... on SearchConfluenceSpace {
						id: entityId
						name: title
						key
						iconPath
						spaceEntity {
							type
							operations { operation target }
						}
					}
				}
			}
		}
	}
}
`;

type SpaceSearchResponse = {
	search?: {
		search?: {
			edges?: Array<{
				node?: {
					iconPath?: string;
					id?: string;
					key?: string;
					name?: string;
					spaceEntity?: {
						operations?: Array<{ operation?: string; target?: string }>;
						type?: string;
					};
					type?: string;
				};
			}>;
		};
	};
};

const SPACE_ENTITY_ATIS = ['ati:cloud:confluence:space'];

// Sync snapshot the picker reads from after the async probe resolves.
const snapshots = new Map<string, ResolvedSpace>();

const cacheKey = (cloudId: string, spaceKey: string): string => `${cloudId}|${spaceKey}`;

/**
 * Pre-flight permission probe consumed directly by the MAUI paste workflow.
 * Returns `false` (so the picker is surfaced) when the probe fails OR the
 * space is not resolvable OR the user lacks `CREATE PAGE`.
 *
 * Side-effect: on success, populates the snapshot cache so the picker can
 * render the current-space chip synchronously via `getCurrentSpaceSnapshot`.
 */
export async function canCreatePageInSpace(cloudId: string, spaceKey: string): Promise<boolean> {
	if (!cloudId || !spaceKey) {
		return false;
	}
	try {
		const response = await performGqlFetch<
			SpaceSearchResponse,
			{ entityAtis: string[]; query: string; siteAri: string; }
		>({
			gqlQuery: ListSpacesWithOperationsQuery,
			headerOptions: { 'X-ExperimentalApi': 'confluence-agg-beta' },
			variables: {
				query: spaceKey,
				siteAri: `ari:cloud:confluence::site/${cloudId}`,
				entityAtis: SPACE_ENTITY_ATIS,
			},
		});

		const edges = response?.data?.search?.search?.edges ?? [];
		for (const edge of edges) {
			const node = edge?.node;
			if (node?.type !== 'space' || node.key !== spaceKey || !node.id) {
				continue;
			}
			const canCreate =
				node.spaceEntity?.operations?.some(
					(op) => op?.operation === 'CREATE' && op?.target === 'PAGE',
				) ?? false;
			snapshots.set(cacheKey(cloudId, spaceKey), {
				canCreatePage: canCreate,
				iconPath: node.iconPath,
				id: node.id,
				key: node.key,
				name: node.name ?? node.key,
				type: node.spaceEntity?.type ?? 'GLOBAL',
			});
			return canCreate;
		}
		return false;
	} catch {
		return false;
	}
}

/**
 * Sync getter for the picker's `currentSpace` chip. Filled by
 * `canCreatePageInSpace`'s side-effect, which always runs before the
 * picker mounts (the picker only mounts after the probe returns `false`).
 */
export function getCurrentSpaceSnapshot(
	cloudId: string,
	spaceKey: string,
): ResolvedSpace | undefined {
	return snapshots.get(cacheKey(cloudId, spaceKey));
}
